import { G, S } from '@mobily/ts-belt';
import cookies from 'browser-cookies';
import { clientAuthAxios } from '../clientAuth';
import { wafClientAuthAxios } from '../awsWaf';
import { browserLogger } from '../../../loggers/browser';
import type { LoginAuthResponse } from './types';
export const ALLOWED_DOMAINS = ['https://helpcentertesting.stitchfix.com', 'https://support.stitchfix.com', 'https://www.stitchfix.com', 'https://stitchfix.com'] as const;
export const redirectUrlFromQueryParam = () => {
  const params = new URLSearchParams(window.location.search);

  // We typically want to redirect the user to the original url they were
  // attempting to view before being prompted to log in. However, in the
  // case of the ungated PDP, we need the ability to forward users to a
  // specific url via a `ret` param.
  const redirectParam = params.get('ret');

  // Catch if the sign-in is from Help Center chat widget
  // =====================================================================
  // A separate 'set'/'not set' 'fromchatwidget' flag param is used instead of
  // the 'ret' param due to the desired ToClientHome url having a different
  // subdomain. This will ensure there is only one way, and one place to
  // redirect someone away from https://stitchfix.com
  const fromChatWidget = params.get('fromchatwidget') !== null;
  if (fromChatWidget) {
    return 'https://support.stitchfix.com/hc/p/messaging-login';
  }
  if (G.isString(redirectParam) && S.isNotEmpty(redirectParam)) {
    let decodedRedirectParam: string;
    try {
      decodedRedirectParam = decodeURIComponent(redirectParam);
    } catch {
      return null;
    }

    // Absolute URL: allow only explicit trusted origins.
    try {
      const parsedUrl = new URL(decodedRedirectParam);
      if ((parsedUrl.protocol === 'https:' || parsedUrl.protocol === 'http:') && ALLOWED_DOMAINS.includes(parsedUrl.origin as (typeof ALLOWED_DOMAINS)[number])) {
        return parsedUrl.toString();
      }
      return null;
    } catch {
      // Not an absolute URL; continue and treat as a relative path.
    }

    // Relative URL: normalize to a safe path on stitchfix.com.
    if (decodedRedirectParam.indexOf('/') !== 0) {
      decodedRedirectParam = `/${decodedRedirectParam}`;
    }
    return `https://www.stitchfix.com${decodedRedirectParam}`;
  }
  return null;
};
export const getRedirectUrl = () => {
  const queryRedirect = redirectUrlFromQueryParam();
  // Preserve the original short-circuit: only read the cookie when the query
  // param didn't yield a redirect.
  const cookieRedirect = queryRedirect ? null : cookies.get('ret');
  const redirectUrl = queryRedirect || cookieRedirect;
  let redirectSource: 'query' | 'cookie' | 'none' = 'none';
  if (queryRedirect) {
    redirectSource = 'query';
  } else if (cookieRedirect) {
    redirectSource = 'cookie';
  }

  // CIAM-4343 diagnostic: capture what we actually resolve as `redirect_url` so we
  // can determine whether the double-encoded / empty-host failures seen at click
  // time originate here (the `ret` cookie fallback, which — unlike the query path —
  // is never run through `new URL()`) or downstream in the magic-link build
  // (client-service / blueshift). Observes only; the return value is unchanged.
  // The diagnostic must never break login. Everything that could throw (URL
  // parsing, the Datadog SDK) is wrapped so a logging failure cannot prevent the
  // redirect from resolving or the request from being sent.
  try {
    let parsedHost = '';
    try {
      if (redirectUrl) parsedHost = new URL(redirectUrl).host;
    } catch {
      parsedHost = '';
    }
    browserLogger({
      level: 'info',
      team: 'client-identity-access-management',
      feature: 'passwordless-login',
      message: 'CIAM-4343 passwordless_login redirect diagnostic',
      context: {
        redirect_source: redirectSource,
        redirect_present: Boolean(redirectUrl),
        // Host only (never the full URL) to avoid logging marketing/PII params.
        redirect_parsed_host: parsedHost,
        // Mirrors the backend failure: a value that parses to an empty host.
        redirect_host_empty: Boolean(redirectUrl) && parsedHost === '',
        // Whole-URL percent-encoding of the scheme, single OR multi-encoded:
        // `https%3A...`, `https%253A...` (`%25` = an encoded `%`), and deeper.
        // Anchored to the start so nested params (`af_dp=https%3A%2F%2F...`) don't match.
        redirect_scheme_encoded: /^https?%(25)*3a/i.test(redirectUrl ?? ''),
        has_ret_query_param: new URLSearchParams(window.location.search).has('ret'),
        // Join key to backend `visitor_id` / `original_visitor_id`. The
        // browser-logs SDK doesn't attach the visitor (only RUM does).
        visitor_id: cookies.get('visitor_uuid') ?? ''
      }
    });
  } catch {
    // Observability must not affect the login flow.
  }
  return redirectUrl;
};

/**
 * Submits a request for passwordless login
 * If the request fails, the Promise will be rejected with an error containing the message
 */
export const requestPasswordlessLogin = (email: string) => {
  const redirectUrl = getRedirectUrl();
  return wafClientAuthAxios.post('passwordless_login', {
    email,
    target_platform: 'web',
    ...(redirectUrl && {
      redirect_url: redirectUrl
    })
  });
};
export const loginByEmail = (email: string, password: string, recaptchaToken?: string, isWafEnabled = false) => {
  const apiService = isWafEnabled ? wafClientAuthAxios : clientAuthAxios;
  return apiService.post<LoginAuthResponse>('session', {
    user: {
      login: email,
      password,
      recaptchaToken
    },
    scope: 'default'
  });
};
export const loginByAppleToken = (userId: string, authCode: string, isWafEnabled = false) => {
  const apiService = isWafEnabled ? wafClientAuthAxios : clientAuthAxios;
  return apiService.post<LoginAuthResponse>('linked_accounts/apple/login', {
    user_id: userId,
    auth_code: authCode,
    scope: 'default'
  });
};
export const loginByFacebookToken = (facebookToken: string, isWafEnabled = false) => {
  const apiService = isWafEnabled ? wafClientAuthAxios : clientAuthAxios;
  return apiService.post<LoginAuthResponse>('linked_accounts/facebook/login', {
    token: facebookToken,
    scope: 'default'
  });
};
export const loginByGoogleToken = (googleToken: string, isWafEnabled = false) => {
  const apiService = isWafEnabled ? wafClientAuthAxios : clientAuthAxios;
  return apiService.post<LoginAuthResponse>('linked_accounts/google/login', {
    token: googleToken,
    scope: 'default'
  });
};